import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';

import { branding } from '@/config/branding';
import { themeConfig } from '@/config/theme.config';
import { AuthProvider } from '@/hooks/useAuth';
import { TimezoneProvider } from '@/hooks/useTimezone';
import '@/lib/i18n';
import { router } from '@/router';

import '@/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A regulatory corpus is reloaded by an ETL run, not second by second.
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider
      attribute={themeConfig.attribute}
      defaultTheme={branding.defaultTheme}
      themes={[...themeConfig.themes]}
      storageKey={themeConfig.storageKey}
      enableSystem={false}
      // The colour scheme is declared per theme in index.css. Left on,
      // next-themes writes `style="color-scheme: <default>"` for any theme it
      // does not recognise -- and it recognises only `light` and `dark`, so
      // `clean` got the dark scheme and its inline style beat the stylesheet.
      enableColorScheme={false}
    >
      <QueryClientProvider client={queryClient}>
        {/* Above AuthProvider so the timezone is available to every screen,
            including the ones rendered before a session resolves. */}
        <TimezoneProvider>
          <AuthProvider>
            <RouterProvider router={router} />
            <Toaster richColors position="top-right" />
          </AuthProvider>
        </TimezoneProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
